// Synthesized J-pop style ambient: 4-bar I–vi–IV–V loop in C major,
// pentatonic melody over walking bass, 4-on-the-floor kick + offbeat hats
// + backbeat snaps. Lookahead scheduler keeps timing precise even when
// the JS thread stutters.

export interface AmbientHandle {
  setVolume(v: number): void;
  stop(): void;
}

const TEMPO_BPM = 112;
const STEP_S = 60 / TEMPO_BPM / 4;        // sixteenth-note duration
const STEPS_PER_BAR = 16;
const BARS = 4;
const TOTAL_STEPS = STEPS_PER_BAR * BARS;
const TICK_MS = 25;
const LOOKAHEAD_S = 0.12;

// MIDI numbers (C4 = 60); null = rest. 64 sixteenths = 4 bars.
const MELODY: (number | null)[] = [
  // Bar 1 — C major
  67, null, 69, null, 72, null, 69, null,  67, null, 64, null, 62, null, null, null,
  // Bar 2 — A minor
  64, null, 67, null, 69, null, 67, null,  64, null, 62, null, 60, null, null, null,
  // Bar 3 — F major
  62, null, 64, null, 67, null, 69, null,  72, null, 69, null, 67, null, 64, null,
  // Bar 4 — G major
  60, null, 62, null, 64, null, 60, null,  null, null, null, null, null, null, null, null,
];

// One bass note per beat (root–fifth pattern).
const BASS: number[] = [
  48, 55, 48, 55,  // C3 G3
  45, 52, 45, 52,  // A2 E3
  41, 48, 41, 48,  // F2 C3
  43, 50, 43, 50,  // G2 D3
];

// One pad chord per bar (C, Am, F, G).
const PAD_CHORDS: number[][] = [
  [60, 64, 67],
  [57, 60, 64],
  [53, 57, 60],
  [55, 59, 62],
];

const midiToHz = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

export function startJpopAmbient(ctx: AudioContext, destination: AudioNode): AmbientHandle {
  const master = ctx.createGain();
  master.gain.value = 0;
  master.connect(destination);

  // Soft tone bus for melodic voices (drums bypass to stay punchy).
  const tone = ctx.createBiquadFilter();
  tone.type = 'lowpass';
  tone.frequency.value = 4200;
  tone.Q.value = 0.4;
  tone.connect(master);

  // Short feedback delay → light stereo-less "room".
  const delay = ctx.createDelay(1.0);
  delay.delayTime.value = STEP_S * 3;
  const feedback = ctx.createGain();
  feedback.gain.value = 0.22;
  delay.connect(feedback).connect(delay);
  delay.connect(tone);

  // Pre-built noise buffer reused for every hat/snap.
  const noiseLen = Math.floor(0.06 * ctx.sampleRate);
  const noiseBuf = ctx.createBuffer(1, noiseLen, ctx.sampleRate);
  {
    const ch = noiseBuf.getChannelData(0);
    for (let i = 0; i < noiseLen; i += 1) ch[i] = Math.random() * 2 - 1;
  }

  const playLead = (time: number, midi: number) => {
    const f = midiToHz(midi);
    const o = ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(f, time);
    const lp = ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 3200;
    lp.Q.value = 0.9;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(0.32, time + 0.012);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.34);
    o.connect(lp).connect(g);
    g.connect(tone);
    g.connect(delay);
    o.start(time);
    o.stop(time + 0.4);
  };

  const playBass = (time: number, midi: number) => {
    const f = midiToHz(midi);
    const o = ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.setValueAtTime(f, time);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(0.34, time + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.42);
    o.connect(g).connect(tone);
    o.start(time);
    o.stop(time + 0.46);
  };

  const playPadChord = (time: number, chord: number[], duration: number) => {
    for (const m of chord) {
      const o = ctx.createOscillator();
      o.type = 'sine';
      o.frequency.value = midiToHz(m);
      const g = ctx.createGain();
      g.gain.setValueAtTime(0, time);
      g.gain.linearRampToValueAtTime(0.05, time + 0.25);
      g.gain.linearRampToValueAtTime(0.05, time + Math.max(duration - 0.2, 0.3));
      g.gain.linearRampToValueAtTime(0, time + duration);
      o.connect(g).connect(tone);
      o.start(time);
      o.stop(time + duration + 0.05);
    }
  };

  const playKick = (time: number) => {
    const o = ctx.createOscillator();
    o.type = 'sine';
    o.frequency.setValueAtTime(120, time);
    o.frequency.exponentialRampToValueAtTime(45, time + 0.07);
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(0.5, time + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.16);
    o.connect(g).connect(master);
    o.start(time);
    o.stop(time + 0.18);
  };

  const playHat = (time: number) => {
    const n = ctx.createBufferSource();
    n.buffer = noiseBuf;
    const hp = ctx.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 7000;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(0.07, time + 0.002);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.04);
    n.connect(hp).connect(g).connect(master);
    n.start(time);
    n.stop(time + 0.06);
  };

  const playSnap = (time: number) => {
    const n = ctx.createBufferSource();
    n.buffer = noiseBuf;
    const bp = ctx.createBiquadFilter();
    bp.type = 'bandpass';
    bp.frequency.value = 2200;
    bp.Q.value = 1.4;
    const g = ctx.createGain();
    g.gain.setValueAtTime(0.0001, time);
    g.gain.exponentialRampToValueAtTime(0.18, time + 0.004);
    g.gain.exponentialRampToValueAtTime(0.0001, time + 0.06);
    n.connect(bp).connect(g).connect(master);
    n.start(time);
    n.stop(time + 0.08);
  };

  const scheduleStep = (stepIndex: number, time: number) => {
    const inBar = stepIndex % STEPS_PER_BAR;

    const m = MELODY[stepIndex];
    if (m !== null && m !== undefined) playLead(time, m);

    if (inBar % 4 === 0) {
      const beatGlobal = Math.floor(stepIndex / 4);
      playBass(time, BASS[beatGlobal]);
      playKick(time);
    }

    if (inBar === 0) {
      const barIndex = Math.floor(stepIndex / STEPS_PER_BAR);
      playPadChord(time, PAD_CHORDS[barIndex], STEP_S * STEPS_PER_BAR);
    }

    if (inBar === 4 || inBar === 12) playSnap(time);
    if (inBar === 2 || inBar === 6 || inBar === 10 || inBar === 14) playHat(time);
  };

  let nextStep = 0;
  let nextStepTime = ctx.currentTime + 0.05;
  let stopped = false;
  let intervalId: number | null = null;

  const tick = () => {
    if (stopped) return;
    while (nextStepTime < ctx.currentTime + LOOKAHEAD_S) {
      scheduleStep(nextStep, nextStepTime);
      nextStepTime += STEP_S;
      nextStep = (nextStep + 1) % TOTAL_STEPS;
    }
  };

  intervalId = window.setInterval(tick, TICK_MS);
  tick();

  return {
    setVolume(v: number) {
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setTargetAtTime(v, t, 0.12);
    },
    stop() {
      stopped = true;
      if (intervalId !== null) {
        window.clearInterval(intervalId);
        intervalId = null;
      }
      const t = ctx.currentTime;
      master.gain.cancelScheduledValues(t);
      master.gain.setTargetAtTime(0, t, 0.15);
      window.setTimeout(() => {
        try { master.disconnect(); } catch { /* ignore */ }
      }, 800);
    },
  };
}
